// src/Dashboard.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import Layout from "./Components/Layout";
import { api } from "./lib/api";
import { useAuth } from "./auth/AuthContext";

import "./accounts/styles/account.css";

import AccountShell from "./accounts/AccountShell";
import AccountHome from "./accounts/pages/AccountHome";
import AccountBirthdays from "./accounts/pages/AccountBirthdays";
import AccountOrders from "./accounts/pages/AccountOrders";
import AccountSettings from "./accounts/pages/AccountSettings";

import AdminHoroscopePanel from "./Components/admin/AdminHoroscopePanel";
import AdminUsers from "./accounts/pages/AdminUsers";
import AdminHome from "./accounts/pages/AdminHome";
import AdminSecurityPanel from "./accounts/pages/AdminSecurityPanel";

export default function Dashboard() {
  const nav = useNavigate();
  const { user, ready, refreshMe, logout: authLogout } = useAuth();

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [savedBirthdays, setSavedBirthdays] = useState([]);

  useEffect(() => {
    if (ready && !user) {
      nav(`/login?next=${encodeURIComponent("/dashboard")}`, { replace: true });
    }
  }, [ready, user, nav]);

  const loadHistory = useCallback(async () => {
    if (!user) return;
    try {
      setHistoryLoading(true);
      const r = await api.get("account/search-history?limit=50&order=desc");
      setHistory(Array.isArray(r?.data?.items) ? r.data.items : []);
    } catch (e) {
      console.warn("[Dashboard] loadHistory failed:", e?.response?.data || e?.message || e);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("birthdays");
        setSavedBirthdays(r?.data?.rows || []);
      } catch {
        setSavedBirthdays([]);
      }
    })();
  }, [user]);

  const subscriptionText = useMemo(() => {
    const s = user?.subscription;
    if (!s) return "none";
    if (s?.active) return s?.status || "active";
    return s?.status || "none";
  }, [user]);

  const hasElementsAccess = useMemo(() => {
    return !!user && (user?.subscription?.active === true || user?.subscription?.plan === "lifetime");
  }, [user]);

  const isAdmin = useMemo(() => {
    return (
      user?.role === "admin" ||
      user?.isAdmin === true ||
      user?.is_admin === 1 ||
      user?.is_admin === true
    );
  }, [user]);

  const logout = async () => {
    try {
      await authLogout?.();
    } catch {
      // ignore
    } finally {
      try {
        await refreshMe?.();
      } catch {}
      window.location.assign("/");
    }
  };

  const goSubscribe = () => nav("/subscribe");

  const deleteBirthday = async (id) => {
    if (!window.confirm("Delete this saved birthday?")) return;
    try {
      await api.delete(`/birthdays/${id}`);
      setSavedBirthdays((prev) => prev.filter((x) => x.id !== id));
    } catch {}
  };

  if (!ready) {
    return (
      <Layout imageUrl="https://mychinesehoroscope.com/my-chinese-horoscope-finder.png">
        <div className="acctOuter">Loading...</div>
      </Layout>
    );
  }

  if (!user) return null;

  const adminPages = {
    dashboard: { label: "Dashboard", component: () => <AdminHome /> },
    users: { label: "Users", component: () => <AdminUsers /> },
    horoscopes: { label: "Horoscopes", component: () => <AdminHoroscopePanel /> },
    orders: { label: "Orders", component: () => <AccountOrders user={user} /> },
    settings: {
      label: "Settings",
      component: () => (
        <AccountSettings
          mode="admin"
          onRefresh={refreshMe}
          onLogout={logout}
          user={user}
          subscriptionText={subscriptionText}
          hasElementsAccess={hasElementsAccess}
        />
      ),
    },
    security: { label: "Security", component: () => <AdminSecurityPanel /> },
  };

  const normalPages = {
    dashboard: {
      label: "Dashboard",
      component: () => (
        <AccountHome
          historyCount={history.length}
          birthdaysCount={savedBirthdays.length}
          subscriptionText={subscriptionText}
        />
      ),
    },
    birthdays: {
      label: "Saved birthdays",
      component: () => <AccountBirthdays items={savedBirthdays} onDelete={deleteBirthday} />,
    },
    settings: {
      label: "Settings",
      component: () => (
        <AccountSettings
          mode="user"
          user={user}
          subscriptionText={subscriptionText}
          hasElementsAccess={hasElementsAccess}
          onUpgrade={goSubscribe}
          onRefresh={refreshMe}
          onLogout={logout}
          history={history}
          historyLoading={historyLoading}
          onReloadHistory={loadHistory}
        />
      ),
    },
  };

  const pages = isAdmin ? adminPages : normalPages;

  return (
    <Layout imageUrl="https://mychinesehoroscope.com/my-chinese-horoscope-finder.png">
      <div className="acctOuter">
        <AccountShell
          user={user}
          subscriptionText={subscriptionText}
          hasElementsAccess={hasElementsAccess}
          onUpgrade={goSubscribe}
          onRefresh={refreshMe}
          onLogout={logout}
          pages={pages}
          isAdmin={!!isAdmin}
        />
      </div>
    </Layout>
  );
}
